// ============================================================
// Printing System Types
// ============================================================

// ---- ZPL Label Builder Types ----

export interface TextOptions {
  x?: number | string;
  y?: number | string;
  fontSize?: number | string;
  fontType?: string;
  orientation?: 'N' | 'R' | 'I' | 'B';
  alignment?: 'L' | 'C' | 'R';
  rotation?: number;
}

export interface FieldBlockOptions {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  fontSize: number | string;
  max?: number;
  alignment?: 'L' | 'C' | 'R';
}

export interface BarcodeOptions {
  x?: number | string;
  y?: number | string;
  height?: number | string;
  type?: string;
  moduleWidth?: number;
  wideBarToNarrowBarWidthRatio?: number;
  humanReadable?: 'Y' | 'N';
}

export interface QRCodeOptions {
  x?: number | string;
  y?: number | string;
  model?: number;
  magnificationFactor?: number;
  errorCorrection?: 'H' | 'Q' | 'M' | 'L';
  maskValue?: number;
}

export interface ImageOptions {
  x?: number | string;
  y?: number | string;
  scaleX?: number;
  scaleY?: number;
}

export interface BoxOptions {
  x?: number | string;
  y?: number | string;
  width: number | string;
  height: number | string;
  borderThickness?: number;
  borderColor?: 'B' | 'W';
  cornerRounding?: number;
}

export interface LineOptions {
  x?: number | string;
  y?: number | string;
  width: number | string;
  height: number | string;
  thickness?: number;
}

export interface InverseBoxOptions {
  x?: number | string;
  y?: number | string;
  width: number | string;
  height: number | string;
  borderThickness?: number | string;
  borderColor?: 'B' | 'W';
  cornerRounding?: number | string;
}

// ---- Label Element Types (for rendering) ----

export interface TextElement {
  type: 'text';
  x: number;
  y: number;
  text: string;
  fontSize: number;
  fontType: string;
  rotation: number;
  orientation: string;
  alignment: string;
}

export interface FieldBlockElement {
  type: 'fieldblock';
  x: number;
  y: number;
  width: number;
  data: string;
  fontSize: number;
  max: number;
  alignment: string;
}

export interface BarcodeElement {
  type: 'barcode';
  x: number;
  y: number;
  data: string;
  height: number;
  barcodeType: string;
  moduleWidth: number;
  ratio: number;
  humanReadable: string;
}

export interface QRCodeElement {
  type: 'qrcode';
  x: number;
  y: number;
  data: string;
  model: number;
  magnificationFactor: number;
  errorCorrection: string;
  maskValue: number;
}

export interface ImageElement {
  type: 'image';
  x: number;
  y: number;
  imageName: string;
  scaleX: number;
  scaleY: number;
}

export interface BoxElement {
  type: 'box';
  x: number;
  y: number;
  width: number;
  height: number;
  borderThickness: number;
  borderColor: string;
  cornerRounding: number;
}

export interface LineElement {
  type: 'line';
  x: number;
  y: number;
  width: number;
  height: number;
  thickness: number;
}

export interface InverseBoxElement {
  type: 'inverse-box';
  x: number;
  y: number;
  width: number;
  height: number;
  borderThickness: number;
  borderColor: string;
  cornerRounding: number;
}

export type LabelElement =
  | TextElement
  | FieldBlockElement
  | BarcodeElement
  | QRCodeElement
  | ImageElement
  | BoxElement
  | LineElement
  | InverseBoxElement;

// ---- Template Definition Types ----

export interface LabelTemplateElement {
  type: 'text' | 'barcode' | 'qrcode' | 'image' | 'box' | 'line' | 'inverse-box' | 'fieldblock';
  content?: string;
  options?:
    | TextOptions
    | BarcodeOptions
    | QRCodeOptions
    | ImageOptions
    | BoxOptions
    | FieldBlockOptions
    | LineOptions
    | InverseBoxOptions;
}

export interface LabelTemplate {
  id: string;
  name: string;
  description: string | null;
  width_dots: number;
  height_dots: number;
  rotation: number;
  elements: LabelTemplateElement[];
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ---- Print Job Types ----

export type PrintJobStatus = 'pending' | 'printing' | 'completed' | 'failed' | 'cancelled';
export type PrintTransportType = 'usb' | 'bluetooth' | 'network' | 'clipboard';

export interface PrintJob {
  id: string;
  job_number: string;
  status: PrintJobStatus;
  label_template_id: string | null;
  transport: PrintTransportType;
  printer_name: string | null;
  printer_address: string | null;
  total_labels: number;
  labels_printed: number;
  generated_zpl: string | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
  // Joined relations
  template?: LabelTemplate;
  items?: PrintJobItem[];
}

export interface PrintJobItem {
  id: string;
  print_job_id: string;
  asset_id: string;
  quantity: number;
  generated_zpl: string | null;
  sort_order: number;
  created_at: string;
  // Joined
  asset?: import('./index').Asset;
}

// ---- Print Selection Types ----

export interface PrintAssetSelection {
  asset: import('./index').Asset;
  quantity: number;
}

// ---- Printer Discovery Types ----

export interface DiscoveredPrinter {
  id: string;
  name: string;
  transport: PrintTransportType;
  address?: string;
  device?: USBDevice | BluetoothDevice;
  status: 'available' | 'connected' | 'busy' | 'error';
}

// ---- Template Variable Placeholders ----

export const TEMPLATE_VARIABLES = {
  '{AssetType}': 'Asset type/category',
  '{SerialNumber}': 'Serial number',
  '{TagId}': 'Unique tag ID',
  '{CustomerName}': 'Customer name',
  '{Status}': 'Asset status',
  '{QRUrl}': 'Public asset URL for QR code',
  '{PrintDate}': 'Date label was printed',
  '{JobNumber}': 'Print job number',
  '{PrintedBy}': 'Name of person who printed',
  '{CompanyName}': 'Company/brand name',
  '{CreatedDate}': 'Asset creation date',
} as const;

export type TemplateVariable = keyof typeof TEMPLATE_VARIABLES;
